import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { exportAnalyticsCsv, updateAnalyticsSnapshot } from "@/features/admin/api/adminAnalyticsClient";
import { analyticsTabs, type AnalyticsRangePreset } from "@/features/admin/adminAnalytics.types";
import { AiUsageCostAnalyticsTab } from "@/features/admin/components/analytics/AiUsageCostAnalyticsTab";
import { ContentGenresAnalyticsTab } from "@/features/admin/components/analytics/ContentGenresAnalyticsTab";
import { OverviewAnalyticsTab } from "@/features/admin/components/analytics/OverviewAnalyticsTab";
import { PlatformHealthAnalyticsTab } from "@/features/admin/components/analytics/PlatformHealthAnalyticsTab";
import { PqrSupportAnalyticsTab } from "@/features/admin/components/analytics/PqrSupportAnalyticsTab";
import { RevenueSubscriptionsAnalyticsTab } from "@/features/admin/components/analytics/RevenueSubscriptionsAnalyticsTab";
import { UsersAccountsAnalyticsTab } from "@/features/admin/components/analytics/UsersAccountsAnalyticsTab";
import { useAdminAnalyticsStore } from "@/stores/adminAnalyticsStore";

const presetOptions: AnalyticsRangePreset[] = [
  "last_7_days",
  "last_30_days",
  "last_90_days",
  "this_month",
  "last_month",
  "custom",
];

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function todayUtcDate() {
  return new Date().toISOString().slice(0, 10);
}

export function AdminAnalyticsPage() {
  const { t } = useTranslation();
  const {
    activeTab,
    error,
    lastRefreshed,
    liveKPIs,
    loading,
    refresh,
    setActiveTab,
    setTimeRange,
    snapshotData,
    timeRange,
  } = useAdminAnalyticsStore();
  const [customStart, setCustomStart] = useState(timeRange.startDate);
  const [customEnd, setCustomEnd] = useState(timeRange.endDate);
  const [exporting, setExporting] = useState(false);
  const [snapshotUpdate, setSnapshotUpdate] = useState<{
    error: string | null;
    loading: boolean;
    message: string | null;
  }>({
    error: null,
    loading: false,
    message: null,
  });

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const activeSnapshot = snapshotData[activeTab] ?? null;
  const tabLoading = Boolean(loading[activeTab] || loading.live);
  const freshnessNote = activeSnapshot?.notes[0] ?? t("adminWorkspace.analytics.dailyFreshness");
  const lastRefreshedLabel = useMemo(() => (
    lastRefreshed
      ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(lastRefreshed)
      : t("adminWorkspace.analytics.neverLoaded")
  ), [lastRefreshed, t]);

  async function handlePresetChange(nextPreset: AnalyticsRangePreset) {
    if (nextPreset === "custom") {
      await setTimeRange("custom", {
        endDate: customEnd,
        startDate: customStart,
      });
      return;
    }

    await setTimeRange(nextPreset);
  }

  async function handleExport() {
    setExporting(true);
    try {
      const blob = await exportAnalyticsCsv(activeTab, timeRange);
      downloadBlob(blob, `analytics-${activeTab}-${timeRange.startDate}-to-${timeRange.endDate}.csv`);
    } finally {
      setExporting(false);
    }
  }

  async function handleSnapshotUpdate() {
    setSnapshotUpdate({ error: null, loading: true, message: null });
    try {
      const result = await updateAnalyticsSnapshot(todayUtcDate());
      await refresh();
      setSnapshotUpdate({
        error: null,
        loading: false,
        message: t("adminWorkspace.analytics.snapshotUpdated", {
          count: result.metricRows,
          date: result.targetDate,
          defaultValue: "Analytics data updated for {{date}} with {{count}} rows.",
        }),
      });
    } catch (error) {
      setSnapshotUpdate({
        error: error instanceof Error ? error.message : "ANALYTICS_LOAD_FAILED",
        loading: false,
        message: null,
      });
    }
  }

  return (
    <div className="space-y-5">
      <section className="rounded-[1.7rem] border border-white/8 bg-white/[0.035] px-5 py-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.34em] text-[#b69d7e]">
          {t("adminWorkspace.analytics.eyebrow")}
        </p>
        <div className="mt-3 flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-3xl">
            <h2 className="font-display text-4xl text-[#fff7ef]">
              {t("adminWorkspace.analytics.title")}
            </h2>
            <p className="mt-3 text-sm leading-7 text-[#d7cab9]/76">
              {t("adminWorkspace.analytics.body")}
            </p>
            <p className="mt-2 text-xs text-[#d7cab9]/65">{freshnessNote}</p>
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <label className="grid gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#b69d7e]">
              {t("adminWorkspace.analytics.range")}
              <select
                className="rounded-full border border-white/10 bg-black/20 px-4 py-3 text-sm normal-case tracking-normal text-[#fff7ef] outline-none"
                value={timeRange.preset}
                onChange={(event) => void handlePresetChange(event.target.value as AnalyticsRangePreset)}
              >
                {presetOptions.map((preset) => (
                  <option key={preset} value={preset}>
                    {t(`adminWorkspace.analytics.presets.${preset}`)}
                  </option>
                ))}
              </select>
            </label>

            {timeRange.preset === "custom" ? (
              <div className="grid grid-cols-2 gap-2">
                <input
                  className="rounded-full border border-white/10 bg-black/20 px-4 py-3 text-sm text-[#fff7ef] outline-none"
                  type="date"
                  value={customStart}
                  onChange={(event) => setCustomStart(event.target.value)}
                  onBlur={() => void setTimeRange("custom", { endDate: customEnd, startDate: customStart })}
                />
                <input
                  className="rounded-full border border-white/10 bg-black/20 px-4 py-3 text-sm text-[#fff7ef] outline-none"
                  type="date"
                  value={customEnd}
                  onChange={(event) => setCustomEnd(event.target.value)}
                  onBlur={() => void setTimeRange("custom", { endDate: customEnd, startDate: customStart })}
                />
              </div>
            ) : null}

            <Button disabled={tabLoading} type="button" variant="outline" onClick={() => void refresh()}>
              <RefreshCw className="mr-2 size-4" />
              {t("adminWorkspace.analytics.refresh")}
            </Button>
            <Button
              disabled={snapshotUpdate.loading}
              type="button"
              variant="outline"
              onClick={() => void handleSnapshotUpdate()}
            >
              <RefreshCw className="mr-2 size-4" />
              {snapshotUpdate.loading
                ? t("adminWorkspace.analytics.updatingSnapshot", "Updating data...")
                : t("adminWorkspace.analytics.updateSnapshot", "Update data now")}
            </Button>
            <Button disabled={exporting} type="button" onClick={() => void handleExport()}>
              <Download className="mr-2 size-4" />
              {t("adminWorkspace.analytics.downloadCsv")}
            </Button>
          </div>
        </div>

        <p className="mt-4 text-xs text-[#d7cab9]/65">
          {t("adminWorkspace.analytics.lastRefreshed", { value: lastRefreshedLabel })}
        </p>
        {snapshotUpdate.message ? (
          <p className="mt-2 text-xs text-[#bfd7b4]">{snapshotUpdate.message}</p>
        ) : null}
        {snapshotUpdate.error ? (
          <p className="mt-2 text-xs text-[#f6d8d2]">{snapshotUpdate.error}</p>
        ) : null}
      </section>

      {error ? (
        <div className="rounded-[1.4rem] border border-[#7b3430]/35 bg-[#6d2d29]/25 px-5 py-4 text-sm text-[#f6d8d2]">
          {error}
        </div>
      ) : null}

      <div className="flex gap-2 overflow-x-auto rounded-[1.35rem] border border-white/8 bg-black/20 p-2">
        {analyticsTabs.map((tab) => (
          <button
            key={tab}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition ${
              activeTab === tab
                ? "bg-[#e8c59b] text-[#21160f]"
                : "text-[#d7cab9]/76 hover:bg-white/8 hover:text-[#fff7ef]"
            }`}
            type="button"
            onClick={() => void setActiveTab(tab)}
          >
            {t(`adminWorkspace.analytics.tabs.${tab}`)}
          </button>
        ))}
      </div>

      {activeTab === "overview" ? (
        <OverviewAnalyticsTab liveKPIs={liveKPIs} loading={tabLoading} snapshot={activeSnapshot} />
      ) : activeTab === "users" ? (
        <UsersAccountsAnalyticsTab loading={tabLoading} snapshot={activeSnapshot} />
      ) : activeTab === "revenue" ? (
        <RevenueSubscriptionsAnalyticsTab loading={tabLoading} snapshot={activeSnapshot} />
      ) : activeTab === "ai" ? (
        <AiUsageCostAnalyticsTab liveKPIs={liveKPIs} loading={tabLoading} snapshot={activeSnapshot} />
      ) : activeTab === "content" ? (
        <ContentGenresAnalyticsTab loading={tabLoading} snapshot={activeSnapshot} />
      ) : activeTab === "pqr" ? (
        <PqrSupportAnalyticsTab loading={tabLoading} snapshot={activeSnapshot} />
      ) : activeTab === "health" ? (
        <PlatformHealthAnalyticsTab liveKPIs={liveKPIs} loading={tabLoading} snapshot={activeSnapshot} />
      ) : (
        <section className="rounded-[1.45rem] border border-white/10 bg-black/20 p-8 text-sm text-[#d7cab9]/72">
          {t("adminWorkspace.analytics.pendingTab")}
        </section>
      )}
    </div>
  );
}
